'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { BlockList } from '@udemy-v2/react-core-components';
import { useCommonData } from '@udemy-v2/common-data';
import { useClpNoticeContext } from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import { ExperimentImpressionEvent } from '@udemy-v2/react-experimentation';
import { Icon } from '@udemy-v2/icons';
import styles from './subscription-purchase-content.module.scss';
import { LocalizedHtml, useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { CollapsiblePurchaseSection } from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, PlanPeriod, PlanPeriodStyle, SubscribeFromPageCookie, SubscriptionCTA, SubscriptionCTAClickEvent, SubscriptionLearnMoreEvent, SubscriptionOfferPresented, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { BlendedOfferBadge } from '@/features/course-landing-page/components/blended-offer-badge/blended-offer-badge.react-component';
import { LifetimeAccessModal } from '@/features/course-landing-page/components/lifetime-access-modal/lifetime-access-modal.react-component';
import style from '../non-catalog-pp-offer/non-catalog-pp-offer.module.scss';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { CollapsiblePurchaseBoxContent, SUBSCRIPTION_SUB_VALUE_PROP_MSG } from './collapsible-purchase-box-content.react-component';
const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
interface CategoryStats {
  isSubCopyEnabled?: boolean;
}
const CollapsibleSubscriptionPurchaseContentInner = categoryStats => {
  const $ = _c(101);
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const {
    unwrappedConsumerSubscriptionPlan,
    subscriptionCTAConfiguration,
    subscriptionTiersEnabled,
    isEligibleForBlendedOffer,
    blendedOfferFeature,
    clpGenericUdemyPlansCopyEnabled,
    starterPlanUpsellCLPCopyUpdatesEnabled
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption,
    setSelectedPurchaseOption
  } = useClpSelectedOption();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["change_cta_language_for_pp"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  const changeCtaLangFeatureAssignment = featureVariantAssignments.get("change_cta_language_for_pp")?.configuration as {
    getStarted: boolean;
    getPersonalPlan: boolean;
  } | undefined;
  const {
    isCourseInValuePlanCatalog,
    subscribedProductType
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const course = useClpCourse();
  const commonData = useCommonData();
  const clpNoticeContext = useClpNoticeContext();
  const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(_temp)) : FALLBACK_COLLECTION_STATS;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const personalPlanCourseCount = personalPlanStats && "numberOfCourses" in personalPlanStats ? personalPlanStats.numberOfCourses : isGwgStandaloneSubscriber && contentCollections ? subscriptionPlanStats.numberOfCourses : PERSONAL_PLAN_COURSE_COUNT;
  const isPersonalPlanPrimaryPurchaseOptions = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
  const isStarterCourseInTiers = subscriptionTiersEnabled && isCourseInValuePlanCatalog;
  const hidePersonalPlanValuePropForStarterUpsell = isStarterCourseInTiers && starterPlanUpsellCLPCopyUpdatesEnabled;
  const formattedDiscountMonthlyPrice = unwrappedConsumerSubscriptionPlan?.selectedDiscount?.formattedSubscriptionDiscountMonthlyPrice;
  let t1;
  if ($[1] !== gettext || $[2] !== subscriptionPriceBoxNotice?.subtitle_link_text) {
    t1 = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext("Learn more");
    $[1] = gettext;
    $[2] = subscriptionPriceBoxNotice?.subtitle_link_text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const learnMoreText = t1;
  let t2;
  bb0: {
    const base = unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? "https://udemy.com/personal-plan/";
    if (!isEligibleForBlendedOffer) {
      t2 = base;
      break bb0;
    }
    let t3;
    if ($[4] !== base || $[5] !== course.id) {
      const url = new URL(base);
      url.searchParams.set("blended_offer", course.id);
      t3 = url.toString();
      $[4] = base;
      $[5] = course.id;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = t3;
  }
  const subscriptionContextLearnMoreUrl = t2;
  const learnMoreUrl = isStarterCourseInTiers ? "https://udemy.com/pricing/" : subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
  const learnMoreTarget = subscriptionPriceBoxNotice?.subtitle_link_target === "new_tab" ? "_blank" : "_self";
  let t3;
  if ($[7] !== course.id || $[8] !== learnMoreUrl || $[9] !== unwrappedConsumerSubscriptionPlan) {
    t3 = () => {
      if (SUBSCRIPTION_ATTRIBUTION_PATHS.some(path => learnMoreUrl.includes(path))) {
        SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      }
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionLearnMoreEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          uiRegion: UI_REGION,
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[7] = course.id;
    $[8] = learnMoreUrl;
    $[9] = unwrappedConsumerSubscriptionPlan;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleLearnMoreClick = t3;
  let t4;
  if ($[11] !== course.id || $[12] !== unwrappedConsumerSubscriptionPlan) {
    t4 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionCTAClickEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? "trial" : "paid",
          uiRegion: UI_REGION,
          courseId: parseInt(course.id, 10),
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[11] = course.id;
    $[12] = unwrappedConsumerSubscriptionPlan;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleCtaClick = t4;
  let t5;
  if ($[14] !== commonData?.user?.country || $[15] !== course.frontendTrackingId || $[16] !== course.id || $[17] !== subscriptionTiersEnabled || $[18] !== unwrappedConsumerSubscriptionPlan) {
    t5 = subscriptionTiersEnabled && unwrappedConsumerSubscriptionPlan ? () => {
      publishEvent(new SubscriptionOfferPresented({
        subscriptionPlan: unwrappedConsumerSubscriptionPlan,
        productType: unwrappedConsumerSubscriptionPlan.productType,
        countryCode: commonData?.user?.country ?? "",
        courseId: parseInt(course.id, 10),
        courseTrackingId: course.frontendTrackingId ?? null,
        uiRegion: UI_REGION
      }));
    } : undefined;
    $[14] = commonData?.user?.country;
    $[15] = course.frontendTrackingId;
    $[16] = course.id;
    $[17] = subscriptionTiersEnabled;
    $[18] = unwrappedConsumerSubscriptionPlan;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleOfferImpression = t5;
  let t6;
  if ($[20] !== setSelectedPurchaseOption) {
    t6 = () => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION);
    $[20] = setSelectedPurchaseOption;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleExpand = t6;
  const showBlendedOfferBadge = isEligibleForBlendedOffer;
  const [isLifetimeAccessModalOpen, setIsLifetimeAccessModalOpen] = useState(false);
  const hasFiredBlendedOfferImpressionRef = useRef(false);
  let t7;
  if ($[22] !== blendedOfferFeature?.experimentIds || $[23] !== blendedOfferFeature?.isLocalDefault || $[24] !== showBlendedOfferBadge) {
    t7 = () => {
      if (showBlendedOfferBadge && !hasFiredBlendedOfferImpressionRef.current) {
        hasFiredBlendedOfferImpressionRef.current = true;
        publishEvent(new ExperimentImpressionEvent({
          experimentIds: blendedOfferFeature?.experimentIds ?? [],
          isLocalDefaultShown: blendedOfferFeature?.isLocalDefault ?? false
        }));
      }
    };
    $[22] = blendedOfferFeature?.experimentIds;
    $[23] = blendedOfferFeature?.isLocalDefault;
    $[24] = showBlendedOfferBadge;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== blendedOfferFeature || $[27] !== showBlendedOfferBadge) {
    t8 = [showBlendedOfferBadge, blendedOfferFeature];
    $[26] = blendedOfferFeature;
    $[27] = showBlendedOfferBadge;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  useEffect(t7, t8);
  if (!unwrappedConsumerSubscriptionPlan) {
    return null;
  }
  let t9;
  if ($[29] !== gettext) {
    t9 = gettext("Subscribe and save");
    $[29] = gettext;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const titleText = t9;
  let t10;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classNames("ud-text-md");
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== titleText) {
    t11 = <span className={styles["title-row"]}><span className={t10}>{titleText}</span></span>;
    $[32] = titleText;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== formattedDiscountMonthlyPrice || $[35] !== gettext || $[36] !== showBlendedOfferBadge || $[37] !== unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount || $[38] !== unwrappedConsumerSubscriptionPlan.trialPeriodDays) {
    t12 = showBlendedOfferBadge ? <p><span className="ud-heading-xl">{unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}</span><span className="ud-text-sm">{gettext("/month, billed yearly")}</span></p> : <PlanPeriod listPriceText={unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount} trialPeriodDays={unwrappedConsumerSubscriptionPlan.trialPeriodDays} style={PlanPeriodStyle.TITLE} discountPriceText={formattedDiscountMonthlyPrice} />;
    $[34] = formattedDiscountMonthlyPrice;
    $[35] = gettext;
    $[36] = showBlendedOfferBadge;
    $[37] = unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount;
    $[38] = unwrappedConsumerSubscriptionPlan.trialPeriodDays;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const title = t13;
  let t14;
  if ($[43] !== gettext) {
    t14 = config => {
      if (!config) {
        return gettext("Start subscription");
      }
      if (config.getStarted && !config.getPersonalPlan) {
        return gettext("Get started");
      }
      if (!config.getStarted && config.getPersonalPlan) {
        return gettext("Get Personal Plan");
      }
      return gettext("Start subscription");
    };
    $[43] = gettext;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const ctaText = t14;
  const T0 = CollapsiblePurchaseSection;
  let t15;
  if ($[45] !== isLifetimeAccessModalOpen || $[46] !== showBlendedOfferBadge || $[47] !== unwrappedConsumerSubscriptionPlan.urlTermsOfUse) {
    t15 = showBlendedOfferBadge && <><BlendedOfferBadge termsUrl={unwrappedConsumerSubscriptionPlan?.urlTermsOfUse} onLifetimeAccessClick={() => setIsLifetimeAccessModalOpen(true)} /><LifetimeAccessModal isOpen={isLifetimeAccessModalOpen} onClose={() => setIsLifetimeAccessModalOpen(false)} /></>;
    $[45] = isLifetimeAccessModalOpen;
    $[46] = showBlendedOfferBadge;
    $[47] = unwrappedConsumerSubscriptionPlan.urlTermsOfUse;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const T1 = BlockList;
  const t16 = "tight";
  const t17 = "small";
  let t18;
  if ($[49] !== categoryStats.isSubCopyEnabled || $[50] !== gettext) {
    t18 = categoryStats.isSubCopyEnabled && <div className={styles["value-prop-badge"]}><Icon className={style["value-prop-icon"]} glyph="info-outline" color="info" size="small" label="" /><LocalizedHtml html={gettext(SUBSCRIPTION_SUB_VALUE_PROP_MSG)} interpolate={{
        bold: <span className="ud-text-bold" />
      }} /></div>;
    $[49] = categoryStats.isSubCopyEnabled;
    $[50] = gettext;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const t19 = categoryStats.isSubCopyEnabled && <CollapsiblePurchaseBoxContent coursesCount={formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)} categoryName={course.categories[0]?.name} />;
  const t20 = !categoryStats.isSubCopyEnabled && <BlockList.Item icon={<Icon label="" size="xsmall" glyph={showBlendedOfferBadge ? "tick" : "video-playlist"} />}><span className="ud-text-sm">{showBlendedOfferBadge ? interpolate(gettext("Access %(courses_count)s+ Premium courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true) : isStarterCourseInTiers && clpGenericUdemyPlansCopyEnabled ? interpolate(gettext("Access up to %(courses_count)s+ courses with a Udemy subscription."), {
        courses_count: formatNumber(personalPlanCourseCount)
      }, true) : interpolate(gettext("Access to %(courses_count)s+ top-rated courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true)}</span></BlockList.Item>;
  const t21 = showBlendedOfferBadge ? "tick" : subscriptionTiersEnabled ? "cancel" : "coupon";
  let t22;
  if ($[52] !== t21) {
    t22 = <Icon label="" size="xsmall" glyph={t21} />;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== gettext) {
    t23 = gettext("Cancel anytime");
    $[54] = gettext;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t23) {
    t24 = <span className="ud-text-sm">{t23}</span>;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t22 || $[59] !== t24) {
    t25 = <BlockList.Item icon={t22}>{t24}</BlockList.Item>;
    $[58] = t22;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== categoryStats.isSubCopyEnabled || $[62] !== gettext || $[63] !== handleLearnMoreClick || $[64] !== learnMoreTarget || $[65] !== learnMoreText || $[66] !== learnMoreUrl || $[67] !== showBlendedOfferBadge) {
    t26 = !categoryStats.isSubCopyEnabled && <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}><a className={showBlendedOfferBadge ? styles["learn-more-link-blended"] : styles["learn-more-link"]} data-purpose="subscription-learn-more-link" href={learnMoreUrl} target={learnMoreTarget} onClick={handleLearnMoreClick}>{showBlendedOfferBadge ? gettext("Learn more about Personal Plan") : learnMoreText}</a></BlockList.Item>;
    $[61] = categoryStats.isSubCopyEnabled;
    $[62] = gettext;
    $[63] = handleLearnMoreClick;
    $[64] = learnMoreTarget;
    $[65] = learnMoreText;
    $[66] = learnMoreUrl;
    $[67] = showBlendedOfferBadge;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== T1 || $[70] !== t18 || $[71] !== t19 || $[72] !== t20 || $[73] !== t25 || $[74] !== t26) {
    t27 = <T1 padding={t16} size={t17}>{t18}{t19}{t20}{t25}{t26}</T1>;
    $[69] = T1;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
    $[73] = t25;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const T2 = SubscriptionCTA;
  let t28;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = classNames(styles["subscription-cta"], styles["subscription-cta-collapsible"]);
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  const t29 = UI_REGION;
  const t30 = !hidePersonalPlanValuePropForStarterUpsell && categoryStats.isSubCopyEnabled;
  const t31 = "subscription-purchase-button";
  const t32 = "primary";
  const t33 = parseInt(course.id, 10);
  const t34 = false;
  let t35;
  if ($[77] !== subscriptionCTAConfiguration || $[78] !== subscriptionPriceBoxNotice) {
    t35 = subscriptionCTAConfiguration?.upgradeUrl ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: subscriptionCTAConfiguration.upgradeUrl
    } : subscriptionPriceBoxNotice;
    $[77] = subscriptionCTAConfiguration;
    $[78] = subscriptionPriceBoxNotice;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  const t36 = subscriptionCTAConfiguration?.directCheckout;
  const t37 = subscriptionCTAConfiguration?.selectPlanText;
  const t38 = "Join 5 lakh+ learners on Personal Plan today";
  const t39 = subscriptionCTAConfiguration?.purchaseCtaText || ctaText(changeCtaLangFeatureAssignment);
  let t40;
  if ($[80] !== T2 || $[81] !== handleCtaClick || $[82] !== handleOfferImpression || $[83] !== t28 || $[84] !== t29 || $[85] !== t30 || $[86] !== t33 || $[87] !== t35 || $[88] !== t36 || $[89] !== t37 || $[90] !== t39 || $[91] !== unwrappedConsumerSubscriptionPlan) {
    t40 = <T2 className={t28} uiRegion={t29} showSubtitle={t30} data-purpose={t31} offerCtaStyle={t32} onCTAClick={handleCtaClick} trackImpression={handleOfferImpression} courseId={t33} includeTrialDaysOnCta={t34} plan={unwrappedConsumerSubscriptionPlan} noticeData={t35} directCheckout={t36} selectPlanText={t37} customSubtitle={t38} purchaseCtaText={t39} />;
    $[80] = T2;
    $[81] = handleCtaClick;
    $[82] = handleOfferImpression;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
    $[86] = t33;
    $[87] = t35;
    $[88] = t36;
    $[89] = t37;
    $[90] = t39;
    $[91] = unwrappedConsumerSubscriptionPlan;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== T0 || $[94] !== handleExpand || $[95] !== isPersonalPlanPrimaryPurchaseOptions || $[96] !== t15 || $[97] !== t27 || $[98] !== t40 || $[99] !== title) {
    t41 = <T0 expanded={isPersonalPlanPrimaryPurchaseOptions} title={title} onExpand={handleExpand}>{t15}{t27}{t40}</T0>;
    $[93] = T0;
    $[94] = handleExpand;
    $[95] = isPersonalPlanPrimaryPurchaseOptions;
    $[96] = t15;
    $[97] = t27;
    $[98] = t40;
    $[99] = title;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  return t41;
};
export const CollapsibleSubscriptionPurchaseContent = React.memo(CollapsibleSubscriptionPurchaseContentInner);
function _temp(contentCollection) {
  return contentCollection.stats;
}